import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../api';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const UploadPage = () => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Chỉ hỗ trợ file CSV, XLSX, XLS');
      return;
    }

    setUploading(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/analytics/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success(`Import thành công ${res.data.rows_imported || 0} dòng`);
      setHistory((prev) => [{ name: file.name, ...res.data, time: new Date().toLocaleString('vi-VN') }, ...prev]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload thất bại';
      setResult({ success: false, error: detail });
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    disabled: uploading,
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Upload Dữ liệu</h2>
        <p className="text-sm text-gray-500 mt-1">Import dữ liệu lịch sử từ file CSV hoặc Excel</p>
      </div>

      <div
        {...getRootProps()}
        className={`card border-2 border-dashed cursor-pointer transition-all duration-200 text-center py-12 ${
          isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-600">Đang xử lý file...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
              <Upload className="w-8 h-8 text-blue-500" />
            </div>
            <div>
              <p className="font-medium text-gray-800">
                {isDragActive ? 'Thả file vào đây...' : 'Kéo thả file hoặc click để chọn'}
              </p>
              <p className="text-xs text-gray-400 mt-1">Hỗ trợ: CSV, XLSX, XLS</p>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`card border-l-4 ${result.success !== false ? 'border-green-500' : 'border-red-500'}`}
          >
            <div className="flex items-start gap-3">
              {result.success !== false ? (
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              ) : (
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
              )}
              <div>
                <p className="font-medium text-gray-800">
                  {result.success !== false ? 'Import thành công!' : 'Import thất bại'}
                </p>
                {result.rows_imported !== undefined && (
                  <p className="text-sm text-gray-600 mt-1">Đã import: {result.rows_imported} dòng</p>
                )}
                {result.rows_skipped !== undefined && result.rows_skipped > 0 && (
                  <p className="text-sm text-orange-600">Bỏ qua: {result.rows_skipped} dòng</p>
                )}
                {result.error && <p className="text-sm text-red-600 mt-1">{result.error}</p>}
                {result.warnings && result.warnings.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {result.warnings.map((w, i) => (
                      <li key={i} className="text-xs text-orange-500">⚠ {w}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload format guide */}
      <div className="card">
        <h4 className="font-semibold text-gray-800 mb-3">Hướng dẫn định dạng file</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-medium text-gray-500">Cột</th>
                <th className="text-left py-2 px-3 font-medium text-gray-500">Mô tả</th>
                <th className="text-left py-2 px-3 font-medium text-gray-500">Bắt buộc</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-b border-gray-50"><td className="py-2 px-3 font-mono text-xs">customer_id / customer_name</td><td className="py-2 px-3">ID hoặc tên khách hàng</td><td className="py-2 px-3">✅</td></tr>
              <tr className="border-b border-gray-50"><td className="py-2 px-3 font-mono text-xs">message / content</td><td className="py-2 px-3">Nội dung tin nhắn</td><td className="py-2 px-3">✅</td></tr>
              <tr className="border-b border-gray-50"><td className="py-2 px-3 font-mono text-xs">sender_type</td><td className="py-2 px-3">customer / agent</td><td className="py-2 px-3">✅</td></tr>
              <tr className="border-b border-gray-50"><td className="py-2 px-3 font-mono text-xs">timestamp / created_at</td><td className="py-2 px-3">Thời gian gửi</td><td className="py-2 px-3">⬜</td></tr>
              <tr className="border-b border-gray-50"><td className="py-2 px-3 font-mono text-xs">agent_id / agent_name</td><td className="py-2 px-3">ID hoặc tên nhân viên</td><td className="py-2 px-3">⬜</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload history */}
      {history.length > 0 && (
        <div className="card">
          <h4 className="font-semibold text-gray-800 mb-3">Lịch sử upload</h4>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{h.name}</p>
                  <p className="text-xs text-gray-400">{h.time} · {h.rows_imported} dòng</p>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default UploadPage;
